import { type ExpandItem, type ExpandParams, parseExpand } from "./parseExpand.ts";

function isBareExpand(params: ExpandParams): boolean {
    return Object.keys(params).length === 0;
}

function mergeExpandParams(target: ExpandParams, source: ExpandParams): ExpandParams {
    const merged: ExpandParams = { ...target };
    const selectAllWins = isBareExpand(source) || isBareExpand(target);

    if (selectAllWins) {
        delete merged.$select;
    }

    for (const key of Object.keys(source)) {
        if (key === "$select") {
            if (selectAllWins) {
                continue;
            }

            const targetSelect = merged.$select;
            const sourceSelect = source.$select;

            if (targetSelect && sourceSelect) {
                merged.$select = [...new Set([...targetSelect, ...sourceSelect])];
            } else if (sourceSelect) {
                merged.$select = [...sourceSelect];
            }
        } else if (key === "$expand") {
            merged.$expand = mergeExpandItems([...(merged.$expand || []), ...(source.$expand || [])]);
        } else if (key === "$top") {
            merged.$top =
                merged.$top !== undefined && source.$top !== undefined
                    ? Math.max(merged.$top, source.$top)
                    : (source.$top ?? merged.$top);
        } else if (key === "$count") {
            if (source.$count === true || merged.$count === true) {
                merged.$count = true;
            } else if (source.$count === false) {
                merged.$count ??= false;
            }
        } else {
            merged[key] = source[key];
        }
    }

    return merged;
}

function mergeExpandItems(items: ExpandItem[]): ExpandItem[] {
    const grouped = new Map<string, ExpandParams>();

    for (const item of items) {
        const existing = grouped.get(item.entity);
        if (existing) {
            grouped.set(item.entity, mergeExpandParams(existing, item.params));
        } else {
            grouped.set(item.entity, { ...item.params });
        }
    }

    return Array.from(grouped.entries()).map(([entity, params]) => ({ entity, params }));
}

function serializeParamValue(value: NonNullable<ExpandParams[string]>): string {
    if (typeof value === "string") {
        return value;
    }

    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }

    return value.map(entry => (typeof entry === "string" ? entry : serializeExpandItems([entry]))).join(",");
}

export function serializeExpandItems(items: ExpandItem[]): string {
    return items
        .toSorted((a, b) => a.entity.localeCompare(b.entity))
        .map(item => {
            const paramParts: string[] = [];

            if (item.params.$select) {
                paramParts.push(`$select=${[...item.params.$select].toSorted((a, b) => a.localeCompare(b)).join(",")}`);
            }

            if (item.params.$expand) {
                paramParts.push(`$expand=${serializeExpandItems(item.params.$expand)}`);
            }

            for (const [key, value] of Object.entries(item.params).toSorted(([a], [b]) => a.localeCompare(b))) {
                if (key === "$select" || key === "$expand" || value === undefined) {
                    continue;
                }
                paramParts.push(`${key}=${serializeParamValue(value)}`);
            }

            if (paramParts.length === 0) {
                return item.entity;
            }

            return `${item.entity}(${paramParts.join(";")})`;
        })
        .join(",");
}

export function mergeExpands(expands: string[]): string {
    const filtered = expands.filter(Boolean);
    if (filtered.length === 0) {
        return "";
    }

    const joined = filtered.join(",");
    const parsed = parseExpand(joined);
    const merged = mergeExpandItems(parsed);

    return serializeExpandItems(merged);
}
